// Order-time footage link validation: the client-, editor- and staff-facing rendering
// of `GET /projects/{id}/link-checks`. Copy per verdict follows the approved spec:
// a mistyped URL is told exactly how to get the right link; a short share stays soft
// because the client may be right; `unknown` renders NOTHING for a client (not a
// badge, not an empty container) because our failure is never their problem.
import { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, HelpCircle, Clock, MailCheck, RefreshCw , HardDrive } from 'lucide-react';
import api from '@/lib/api';
import { clientRowCopy } from '@/lib/linkVerdictCopy';
import { toast } from 'sonner';

const GB = 1e9;
// Client-facing amber copy uses amber-200, NOT amber-100: amber-100 measured 1.07:1 in the
// light theme (invisible) on 2026-09-09; amber-200 measures 6.9:1 light / 10:1 dark.
const fmtGB = (b) => (b == null ? '' : `${Math.round(b / GB)} GB`);
const fmtN = (n) => (n == null ? '' : n.toLocaleString());

// A Drive folder of ~300 subfolders lists in ~12 s; after Check again, wait up to a minute.
const RECHECK_WAIT_MS = 60000;
// "Sep 14": the date a listing was taken, never how long ago.
const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

// Every mounted useLinkChecks, told when a Check again finishes on its project. The drawer
// reads link checks in three places (header chip, pull chip, Files tab); without this the
// header kept saying "Short" beside a Files tab that had just re-checked.
const recheckListeners = new Set();

export function useLinkChecks(projectId, { poll = false, pollMs = 1500, maxMs = 20000 } = {}) {
  const [data, setData] = useState(null);
  const [settled, setSettled] = useState(false);
  // Bumped by recheck(): re-runs the fetch and polls until every row is done again.
  const [rechecks, setRechecks] = useState(0);
  // Bumped when a Check again on this project finished somewhere else: one fresh fetch.
  const [refreshes, setRefreshes] = useState(0);
  // Set by recheck(), cleared once this hook has told the others. Never set by a refresh,
  // so a refresh cannot announce itself and loop.
  const announce = useRef(false);
  useEffect(() => {
    if (!projectId) return;
    const listener = (id) => { if (id === projectId) setRefreshes((n) => n + 1); };
    recheckListeners.add(listener);
    return () => { recheckListeners.delete(listener); };
  }, [projectId]);
  useEffect(() => {
    if (!projectId) return;
    let stop = false; const t0 = Date.now(); let timer;
    const polling = poll || rechecks > 0;
    const wait = rechecks > 0 ? RECHECK_WAIT_MS : maxMs;
    const tick = async () => {
      try { const r = await api.get(`/projects/${projectId}/link-checks`); if (!stop) setData(r.data);
        const rows = r.data?.rows || [];
        const allDone = rows.length > 0 && rows.every(x => x.status === 'done');
        if (!polling || allDone || Date.now() - t0 > wait) {
          if (!stop) setSettled(true);
          if (!stop && announce.current) { announce.current = false; recheckListeners.forEach((fn) => fn(projectId)); }
          return;
        }
      } catch { if (!stop) setSettled(true); return; }
      timer = setTimeout(tick, pollMs);
    };
    tick();
    return () => { stop = true; clearTimeout(timer); };
  }, [projectId, poll, pollMs, maxMs, rechecks, refreshes]);
  // Unsettled from the click, so the button never flashes back to idle between the POST
  // and the first poll. Throws the request error for the caller to show.
  const recheck = useCallback(async () => {
    setSettled(false);
    try { await api.post(`/projects/${projectId}/link-checks/recheck`); }
    catch (e) { setSettled(true); throw e; }
    announce.current = true;
    setRechecks((n) => n + 1);
  }, [projectId]);
  return { data, settled, recheck };
}

const isStaff = (role) => role === 'admin' || role === 'staff';
// The marginal size note, in words, from the numbers (not the stored note text: rows checked before 2026-09-30 carry
// "8% short of declared; John-only", an internal reminder an editor read as the message). The same best-of-units ratio
// as backend/app/link_check/compare.py: clients declare in GB or GiB, so the closer reading counts.
export function marginalText(size) {
  const listed = Number(size?.listed_bytes) || 0, declared = Number(size?.declared_gb) || 0;
  if (!listed || !declared) return 'A little less than the client declared';
  const best = Math.max(listed / 1e9 / declared, listed / 1024 ** 3 / declared);
  const pct = Math.max(1, Math.round((1 - best) * 100));
  return `${pct}% less than the ${Math.round(declared)} GB the client declared`;
}
const isEditor = (role) => role === 'editor';
const isDrive = (row) => row?.provider === 'gdrive';

// ---- CLIENT copy (per-URL): lib/linkVerdictCopy.js, pure so its node test can hold it ----

// ---- STAFF copy (per-URL) ---------------------------------------------------
function staffRow(row) {
  const grey = 'text-slate-400 border-slate-600/40 bg-white/[0.03]';
  const red = 'text-ug-status-red border-ug-status-red/30 bg-ug-status-red/10';
  const amber = 'text-amber-300 border-amber-400/30 bg-amber-400/10';
  const green = 'text-emerald-300 border-emerald-400/30 bg-emerald-400/10';
  switch (row.verdict) {
    case 'ok': return { cls: green, Icon: CheckCircle2, text: `Checked${row.checked_at ? ` ${fmtDay(row.checked_at)}` : ''}: ${fmtGB(row.listed_bytes)} in ${fmtN(row.listed_files)} files${row.enum_capped ? ' (too many folders to count them all; size not compared)' : ''}` };
    case 'malformed': return { cls: red, Icon: XCircle, text: `Bad link: ${row.reason === 'home_path' ? 'client pasted their own /home/ path' : row.reason.replace(/_/g, ' ')}` };
    // `died_at`: it passed before and died since (the link watch emails the client and the team once).
    case 'dead': return { cls: red, Icon: XCircle, text: `Dead link: ${row.reason}${row.died_at ? ` (it had passed; found dead ${fmtDay(row.died_at)})` : ''}` };
    case 'walled': return { cls: red, Icon: XCircle, text: `No access: ${row.reason}` };
    case 'empty': return { cls: amber, Icon: AlertTriangle, text: row.died_at ? `Emptied: it had files, found empty ${fmtDay(row.died_at)} (re-checking daily for 14 days)` : 'Empty folder (re-checking daily for 14 days)' };
    case 'unknown':
      // A WeTransfer private-API change must read as OUR problem to fix, never as the client's link.
      if ((row.reason || '').startsWith('wetransfer_api_changed')) return { cls: red, Icon: XCircle, text: "WeTransfer's download API changed: the checker and pull tool need a fix (admin)" };
      // Not one of the three: nothing to check, and Footage Pull cannot take it. A person brings it in.
      if (row.reason === 'provider_unsupported') return { cls: amber, Icon: AlertTriangle, text: 'Manual download req’d: not a Dropbox, Google Drive or WeTransfer link, so it is not checked and Footage Pull cannot take it' };
      if (row.reason === 'provider_not_configured') return { cls: grey, Icon: HelpCircle, text: isDrive(row) ? 'Not checked (Drive validation pending)' : 'Not checked' };
      if ((row.attempts || 0) >= 3) return { cls: grey, Icon: HelpCircle, text: `Couldn't check (${(row.reason || '').replace('exception:', '')})` };
      return { cls: grey, Icon: Clock, text: 'Not checked yet' };
    default: return null;
  }
}

/** Per-URL verdict line. Returns null (no DOM at all) when there is nothing to say. */
export function LinkVerdict({ row, role }) {
  if (!row || row.status !== 'done') {
    return isStaff(role) ? <span data-testid="link-verdict-pending" className="tap-auto inline-flex items-center gap-1 text-[9px] font-mono text-slate-500"><Clock className="w-3 h-3" /> Checking…</span> : null;
  }
  if (isStaff(role)) {
    const s = staffRow(row); if (!s) return null;
    return <span data-testid={`link-verdict-${row.verdict}`} className={`tap-auto inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded border ${s.cls}`}><s.Icon className="w-3 h-3" /> {s.text}</span>;
  }
  const copy = clientRowCopy(row);
  if (!copy) return null;
  // A folder not filled yet is a soft amber; one the client emptied after it passed (died_at) needs them, as a dead link does.
  const tone = row.verdict === 'empty' && !row.died_at ? 'text-amber-200/90 border-amber-400/25 bg-amber-400/[0.06]' : 'text-ug-status-red border-ug-status-red/25 bg-ug-status-red/[0.06]';
  return <p data-testid={`link-verdict-${row.verdict}`} className={`text-[11px] leading-relaxed rounded-md border px-3 py-2 mt-1 ${tone}`}>{copy}</p>;
}

/** Project-level size verdict: short / marginal / ok. */
export function LinkCheckSummary({ data, role }) {
  const size = data?.summary?.size; const rows = data?.rows || [];
  if (!size) return null;
  const files = rows.reduce((n, r) => n + (r.listed_files || 0), 0);
  const staff = isStaff(role);
  if (isEditor(role)) {
    // EDITOR voice: informational, never a reason to delay starting. The client copy
    // ("you told us to expect... reply to confirm and we'll proceed") read as "wait"
    // when it reached editors (seen 2026-09-10). Suppressed while any row is still
    // being checked: the summary was computed before the re-check and would be stale.
    if (rows.some((r) => r.status !== 'done')) return null;
    const listed = fmtGB(size.listed_bytes); const declared = size.declared_gb == null ? '' : `${Math.round(size.declared_gb)} GB`;
    const note = 'text-[11px] leading-relaxed rounded-md border px-3 py-2 mt-2 text-amber-200/90 border-amber-400/25 bg-amber-400/[0.06]';
    if (size.verdict === 'short') {
      return <p data-testid="link-check-summary-editor-short" className={note}>Heads up: the total here is smaller than the client said to expect ({listed} vs {declared}). Download it and take a look, then only reach out if something obvious is missing.</p>;
    }
    if (size.verdict === 'ok' && size.size_checked && size.marginal) {
      return <p data-testid="link-check-summary-editor-marginal" className={note}>For your info: the total is a little under what the client expected ({listed} vs {declared}). Nothing to do unless you notice a gap while you're in the files.</p>;
    }
    if (size.verdict === 'ok' && size.size_checked) {
      return <p data-testid="link-check-summary-ok" className="text-[11px] text-emerald-300/90 mt-2 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Link checked: {listed}{files ? ` in ${fmtN(files)} files` : ''}.</p>;
    }
    return null;
  }
  if (size.verdict === 'short') {
    const listed = fmtGB(size.listed_bytes); const declared = `${Math.round(size.declared_gb)} GB`;
    // The size is only as fresh as its oldest listing.
    const oldest = rows.map((r) => r.checked_at).filter(Boolean).sort()[0];
    return staff
      ? <p data-testid="link-check-summary-short" className="tap-auto inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded border text-amber-300 border-amber-400/30 bg-amber-400/10 mt-1"><AlertTriangle className="w-3 h-3" /> Short: found {listed} of {declared}{oldest ? `, checked ${fmtDay(oldest)}` : ''}</p>
      : <p data-testid="link-check-summary-short" className="text-[11px] leading-relaxed rounded-md border px-3 py-2 mt-2 text-amber-200/90 border-amber-400/25 bg-amber-400/[0.06]">We can see <strong>{listed}</strong> in this link, but you told us to expect <strong>{declared}</strong>. If there's another folder, a second camera, audio, or a reception link, please add it below. If {listed} is everything, reply to confirm and we'll proceed.</p>;
  }
  if (size.verdict === 'ok' && size.size_checked) {
    return staff
      ? (size.marginal ? <p data-testid="link-check-summary-marginal" className="tap-auto inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded border text-amber-300/80 border-amber-400/20 bg-amber-400/[0.06] mt-1"><AlertTriangle className="w-3 h-3" /> {marginalText(size)}</p> : null)
      : <p data-testid="link-check-summary-ok" className="text-[11px] text-emerald-300/90 mt-2 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Link checked: {fmtGB(size.listed_bytes)}{files ? ` in ${fmtN(files)} files` : ''}.</p>;
  }
  if (staff && size.capped) return <p data-testid="link-check-summary-capped" className="text-[9px] font-mono text-amber-300/80 mt-1">Too many folders to count them all, so the size was not compared.</p>;
  return null;
}

/** Staff's Check again: re-lists every link on the project now, then the verdicts above
 *  update in place. Nothing renders until the checker knows the project's links. */
export function LinkRecheckButton({ data, settled, role, onRecheck }) {
  const rows = data?.rows || [];
  if (!isStaff(role) || rows.length === 0) return null;
  const checking = !settled || rows.some((r) => r.status !== 'done');
  const run = async () => {
    try { await onRecheck(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not check the link again'); }
  };
  return (
    <button type="button" data-testid="link-check-recheck-btn" disabled={checking} onClick={run}
      className="tap-auto flex items-center gap-1 mt-1.5 text-[9px] text-ug-gold/60 hover:text-ug-gold font-mono transition-colors disabled:opacity-50 disabled:hover:text-ug-gold/60">
      <RefreshCw className={`w-3 h-3 ${checking ? 'animate-spin' : ''}`} /> {checking ? 'Checking…' : 'Check again'}
    </button>
  );
}

/** The board's at-a-glance link state, ONLY while a project sits at Project Requested:
 *  is there a link the pull tool handles, what did the checker make of it, and is the
 *  client still uploading. Pure from the list row (footage_link_status, still_uploading,
 *  detected_footage_services), no fetch: the board is 500 rows polled every 15 s.
 *
 *  One chip for the link, one more only when the client says the upload is in progress.
 *  Nothing renders for a project with no cloud link (hard drive, MediaShuttle): there is
 *  no link to have a state, and a chip saying so on every such row would be noise. */
const LINK_STATE = {
  ok:        ['Link verified', 'text-oxidized-teal border-oxidized-teal/30 bg-oxidized-teal/10', CheckCircle2],
  short:     ['Link short',    'text-amber-300 border-amber-400/30 bg-amber-400/10', AlertTriangle],
  empty:     ['Link empty',    'text-amber-300 border-amber-400/30 bg-amber-400/10', AlertTriangle],
  malformed: ['Bad link',      'text-ug-status-red border-ug-status-red/30 bg-ug-status-red/10', XCircle],
  dead:      ['Dead link',     'text-ug-status-red border-ug-status-red/30 bg-ug-status-red/10', XCircle],
  walled:    ['No access',     'text-ug-status-red border-ug-status-red/30 bg-ug-status-red/10', XCircle],
  unknown:   ['Not verified',  'text-slate-400 border-slate-600/40 bg-white/[0.03]', HelpCircle],
  checking:  ['Checking',      'text-slate-400 border-slate-600/40 bg-white/[0.03] animate-pulse', Clock],
  other:     ['Manual download req’d', 'text-amber-300 border-amber-400/30 bg-amber-400/10', AlertTriangle],
  awaiting:  ['Awaiting footage', 'text-oxidized-teal border-oxidized-teal/30 bg-oxidized-teal/10', Clock],
};
const LINK_STATE_TITLE = {
  ok: 'The checker opened the link and listed files that match what the client declared. Footage Pull will take it.',
  short: 'The link opens, but the files listed add up to less than the client declared. Footage Pull still takes it and tells the editor. Checked again when the upload is marked complete, and daily for 14 days while the project is at Project Requested.',
  empty: 'The link opens but lists no files yet. Re-checked daily.',
  malformed: 'Not a share link the tool can use. Ask the client for a fresh one.',
  dead: 'The link no longer resolves. Ask the client for a fresh share.',
  walled: 'The link needs a password or a login. Ask the client to share it openly.',
  unknown: 'The checker has not reached a verdict on this link: not run yet, or it could not tell. It retries on its own within 15 minutes; if this stays, open the link by hand.',
  checking: 'A link check is running now.',
  other: 'Not a Dropbox, Google Drive or WeTransfer link, so Footage Pull cannot take it. Someone has to download it, upload it to our Dropbox and paste that link as Our Dropbox copy.',
  awaiting: 'The client chose cloud upload and has not sent anything yet: no link, no upload marked complete, no files.',
};
const SUPPORTED = ['dropbox', 'gdrive', 'wetransfer'];
export function LinkStateChip({ project, role, idSuffix = '' }) {
  if (!isStaff(role) || project?.admin_status_id !== 1) return null;
  const fls = project.footage_link_status;
  const hasLink = (fls?.rows || 0) > 0 || (project.detected_footage_services || []).length > 0;
  // No link at all: the one thing the retired green "Footage" pill said that the link
  // state cannot, "the client has sent nothing yet" (cloud upload, not complete, no
  // files). Hard drive / other method projects still show nothing: not a wait on us.
  if (!hasLink && !project.waiting_on_footage) return null;
  if (!hasLink && project.event_upcoming) return null;     // same reason: nothing is due yet
  if (!hasLink) {
    const [label, cls, Icon] = LINK_STATE.awaiting;
    return <span data-testid={`link-state-awaiting${idSuffix}`} title={LINK_STATE_TITLE.awaiting}
                 className={`tap-auto shrink-0 inline-flex items-center gap-1 font-sans text-[8px] font-medium px-1.5 py-0.5 rounded-full border whitespace-nowrap ${cls}`}>
      <Icon className="w-2.5 h-2.5 shrink-0" /> {label}
    </span>;
  }
  // A roll-up written since 2026-09-11 says which providers it saw and the worst verdict
  // among the supported ones; an older roll-up has neither, so fall back to worst_verdict.
  const providers = fls?.providers;
  const noneSupported = Array.isArray(providers) && providers.length > 0 && !providers.some(p => SUPPORTED.includes(p));
  const verdict = fls && 'supported_worst' in fls ? (fls.supported_worst ?? null) : fls?.worst_verdict;
  const key = noneSupported ? 'other'
    : !fls || !fls.checked_at ? 'unknown'
    : (fls.pending || 0) > 0 ? 'checking'
    : (LINK_STATE[verdict] ? verdict : 'unknown');
  // Before the event there cannot be footage, so an empty link is the expected state,
  // not a warning: the "Event <date>" chip beside this one already says it, and saying
  // it twice in amber taught people to ignore amber. The states that need a person
  // regardless of the date (dead, walled, bad link, manual download) still show.
  if (key === 'empty' && project.event_upcoming) return null;
  const [label, cls, Icon] = LINK_STATE[key];
  // When the folder was listed, not when the roll-up was last rewritten (a roll-up from before
  // 2026-09-16 has no listed_at; its own time is the closest it has).
  const listedAt = fls?.listed_at || fls?.checked_at;
  const when = listedAt ? ` Checked ${new Date(listedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.` : '';
  return (
    <>
      <span data-testid={`link-state-${key}${idSuffix}`} title={LINK_STATE_TITLE[key] + when}
            className={`tap-auto shrink-0 inline-flex items-center gap-1 font-sans text-[8px] font-medium px-1.5 py-0.5 rounded-full border whitespace-nowrap ${cls}`}>
        <Icon className="w-2.5 h-2.5 shrink-0" /> {label}
      </span>
    </>
  );
}

// Keyed on FOOTAGE_TRANSFER_OPTIONS (lib/pricing.js), minus the cloud one. Those three are the
// only values a client can choose, so a method not listed here is a cloud upload and shows the
// upload flag instead. A value invented here would simply never match anything.
const NO_UPLOAD_EXPECTED = {
  'Hard Drive': 'Hard drive coming',
  'Other': 'Sending another way',
};

/** The client's submitted upload status, as they set it, on a Project Requested row.
 *  Operator protocol (2026-09-12): staff do not download until the client has marked the
 *  upload complete, so the flag has to be readable at a glance, whatever the link says.
 *  Not a warning, a fact: grey until they start, amber while in progress, teal once
 *  complete. Order-type aware: a "both" order shows video and photo separately when
 *  they differ. Nothing for hard-drive / other-method rows (the flag has no meaning). */
const UPLOAD_STATE = {
  'Not started':      ['Upload not started',  'text-slate-400 border-slate-600/40 bg-white/[0.03]', Clock],
  'Still uploading':  ['Still uploading',     'text-amber-300 border-amber-400/30 bg-amber-400/10', Clock],
  'Upload complete':  ['Upload complete',     'text-oxidized-teal border-oxidized-teal/30 bg-oxidized-teal/10', CheckCircle2],
};
export function uploadFlags(project) {
  const ot = String(project?.order_type || project?.project_type || 'video').toLowerCase();
  const keys = ot === 'photo' ? [['photo', 'photo_upload_status']] : ot === 'both' ? [['video', 'video_upload_status'], ['photo', 'photo_upload_status']] : [['video', 'video_upload_status']];
  return keys
    // A leg coming by hard drive (or any other non-cloud route) has no upload and never will, so
    // its flag means nothing -- and "Upload not started" on a drive that is in the post reads as
    // a client who has done nothing. TransferMethodChip speaks for those legs instead. Per leg,
    // not per project: a "both" order can have the video on a drive and the photos on Dropbox.
    .filter(([label]) => !NO_UPLOAD_EXPECTED[project?.[`${label}_transfer_method`]])
    .map(([label, k]) => [label, project?.[k]]).filter(([, v]) => v && UPLOAD_STATE[v]);
}
/**
 * Has the client been asked to open up a walled Google Drive link, and when.
 *
 * Staff already see the link chip, so this deliberately does NOT repeat that the link
 * is walled. The only thing it adds is the one thing the board could not tell you
 * before: whether anybody has told the client yet (operator, 2026-09-14). It appears
 * only once an email has actually gone, so a project nobody has chased carries nothing.
 *
 * The record is written by the nightly sweep (server `_drive_access_sweep`), two emails
 * maximum, and it survives after the client fixes the link so the history stays legible.
 */
export function DriveAccessChip({ project, role, idSuffix = '' }) {
  const c = project?.drive_access_chase;
  const sent = c?.sent || [];
  if (!isStaff(role) || !sent.length) return null;
  const last = sent[sent.length - 1];
  const when = last?.at ? new Date(last.at) : null;
  const ago = when ? Math.floor((Date.now() - when.getTime()) / 86400000) : null;
  const resolved = c?.stopped === 'resolved';
  const done = sent.length >= 2 && !resolved;
  const [label, cls] = resolved
    ? ['Client fixed access', 'text-oxidized-teal border-oxidized-teal/30 bg-oxidized-teal/10']
    : done
      ? [`Asked ${sent.length}x, no reply`, 'text-ug-status-red border-ug-status-red/30 bg-ug-status-red/10']
      : [`Access asked${sent.length > 1 ? ` ${sent.length}x` : ''}`, 'text-slate-400 border-ug-border bg-white/[0.04]'];
  const dayText = ago === null ? '' : ago === 0 ? 'today' : ago === 1 ? 'yesterday' : `${ago} days ago`;
  return (
    <span data-testid={`drive-access-${resolved ? 'resolved' : done ? 'exhausted' : 'sent'}${idSuffix}`}
      title={`We emailed the client asking them to set the Google Drive link to "Anyone with the link". `
        + `${sent.length} email${sent.length === 1 ? '' : 's'} sent, the last ${dayText}. `
        + (resolved ? 'The link has since checked clean.'
          : done ? 'No more will be sent automatically.'
            : 'One more will go automatically if it stays walled.')}
      className={`tap-auto shrink-0 inline-flex items-center gap-1 font-sans text-[8px] font-medium px-1.5 py-0.5 rounded-full border whitespace-nowrap ${cls}`}>
      <MailCheck className="w-2.5 h-2.5 shrink-0" /> {label}
    </span>
  );
}

/** The three stages a drive goes through, in order: it is in the post, it is here and we are uploading it to our
 *  Dropbox, it is on Dropbox (Our Dropbox copy set). Amber in the middle because that stage is the one that is OURS to
 *  finish. Operator, 2026-09-19, for the NAS ("while we are loading footage ... then once loaded we need to say fully
 *  uploaded"); the NAS stage became Dropbox 2026-10-09 with MediaShuttle retired.
 *
 *  Exported because the copy is the product here, and a second reader would otherwise retype it. */
export function transferStage(project, waitingLabel) {
  if ((project?.footage_copy_link || '').trim()) {
    return ['loaded', 'On Dropbox', 'text-oxidized-teal border-oxidized-teal/30 bg-oxidized-teal/10',
      'The drive is uploaded to our Dropbox (Our Dropbox copy). The editor downloads it with Footage Pull, and the job '
      + 'moves on once its link check passes, the same way a client\'s link moves it.'];
  }
  if (project?.footage_received) {
    return ['loading', 'Uploading to Dropbox', 'text-amber-300 border-amber-400/30 bg-amber-400/10',
      'The drive is here and we are uploading it to our Dropbox. Nothing for the client to do, and nothing for the '
      + 'editor yet. When the upload finishes, paste the link as Our Dropbox copy on the Files tab.'];
  }
  return ['waiting', waitingLabel, 'text-slate-400 border-slate-600/40 bg-white/[0.03]',
    'Nothing to download. We are waiting for it to arrive, and an admin marks it received by hand '
    + 'when it does.'];
}
/** The footage is coming on a hard drive (or any non-cloud route), so there is nothing to
 *  download and nothing to check. Until this existed such a row showed NOTHING at all -- no link
 *  chip, no upload flag, because neither has any meaning here -- and read as a job where the client
 *  had simply done nothing (operator, 2026-09-19, on Ellie and Rick). The absence of a signal is
 *  not a signal; this says the quiet part. */
export function TransferMethodChip({ project, role, idSuffix = '' }) {
  if (!isStaff(role) || project?.admin_status_id !== 1) return null;
  // Once the team has put the drive's footage on our Dropbox ("Our Dropbox copy"), that copy is the footage and the
  // link chip beside this one says how its check went; the drive's own stages no longer apply.
  if ((project?.footage_copy_link || '').trim()) return null;
  const methods = [project?.video_transfer_method, project?.photo_transfer_method].filter(Boolean);
  const hit = methods.find(m => NO_UPLOAD_EXPECTED[m]);
  if (!hit) return null;
  const [stage, label, cls, why] = transferStage(project, NO_UPLOAD_EXPECTED[hit]);
  return (
    <span data-testid={`transfer-method-${stage}${idSuffix}`} title={`${hit}. ${why}`}
      className={`tap-auto shrink-0 inline-flex items-center gap-1 font-sans text-[8px] font-medium px-1.5 py-0.5 rounded-full border whitespace-nowrap ${cls}`}>
      <HardDrive className="w-2.5 h-2.5 shrink-0" /> {label}
    </span>
  );
}

export function UploadStatusChip({ project, role, idSuffix = '' }) {
  if (!isStaff(role) || project?.admin_status_id !== 1) return null;
  const flags = uploadFlags(project);
  if (!flags.length) return null;
  const same = flags.length === 2 && flags[0][1] === flags[1][1];
  const shown = same ? [['', flags[0][1]]] : flags;
  return (
    <>
      {shown.map(([label, v]) => {
        const [text, cls, Icon] = UPLOAD_STATE[v];
        const key = v.toLowerCase().replace(/ /g, '-');
        return (
          <span key={label + v} data-testid={`upload-state-${key}${label ? `-${label}` : ''}${idSuffix}`}
                // "Still uploading" is a WARNING (Ryan, 2026-09-29): do not download yet. The site look paints a
                // data-tone="warning" element's amber in ember; the other two states are not warnings.
                data-tone={v === 'Still uploading' ? 'warning' : undefined}
                title={`The client set this upload to "${v}"${label ? ` (${label})` : ''}. We do not download until it is marked complete; the link check can mark it complete when the listing matches the size they declared.`}
                className={`tap-auto shrink-0 inline-flex items-center gap-1 font-sans text-[8px] font-medium px-1.5 py-0.5 rounded-full border whitespace-nowrap ${cls}`}>
            <Icon className="w-2.5 h-2.5 shrink-0" /> {label && flags.length === 2 ? `${label[0].toUpperCase()}${label.slice(1)}: ` : ''}{text}
          </span>
        );
      })}
    </>
  );
}

/** Staff-only header chip: the project's worst verdict, or nothing when all is well. */
export function LinkCheckChip({ projectId, role }) {
  const { data } = useLinkChecks(isStaff(role) ? projectId : null);
  const w = data?.summary?.worst_verdict; const rows = data?.rows || [];
  if (!isStaff(role) || !w || rows.length === 0) return null;
  // Every link an unsupported provider: the header says what that means for us, not "unchecked".
  if (rows.every(r => r.reason === 'provider_unsupported')) {
    return <span data-testid="link-check-chip-manual" className="tap-auto inline-flex items-center text-[9px] font-mono font-semibold uppercase tracking-wider px-2 py-0.5 rounded border text-amber-300 border-amber-400/30 bg-amber-400/10">Manual download req’d</span>;
  }
  const map = { malformed: ['Bad link', 'text-ug-status-red border-ug-status-red/30 bg-ug-status-red/10'], dead: ['Dead link', 'text-ug-status-red border-ug-status-red/30 bg-ug-status-red/10'],
    walled: ['No access', 'text-ug-status-red border-ug-status-red/30 bg-ug-status-red/10'], short: ['Short', 'text-amber-300 border-amber-400/30 bg-amber-400/10'],
    empty: ['Empty link', 'text-amber-300 border-amber-400/30 bg-amber-400/10'], unknown: ['Unchecked', 'text-slate-400 border-slate-600/40 bg-white/[0.03]'] };
  const m = map[w]; if (!m) return null;
  return <span data-testid={`link-check-chip-${w}`} className={`tap-auto inline-flex items-center text-[9px] font-mono font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${m[1]}`}>{m[0]}</span>;
}

/** Order-complete: poll ~20s while the check runs, then resolve in place. */
const fmtDays = (d) => (d == null ? '' : d === 0 ? 'today' : d === 1 ? '1 day ago' : `${d} days ago`);

/** Drawer-header chip for staff: how the last pull ended, or that a token went out and nothing ever completed.
 *  Thresholds are deliberately absent: the chip shows the raw state until a few weeks of real ones say what normal is. */
export function PullStateChip({ data, role }) {
  const pull = data?.pull;
  if (!isStaff(role) || !pull) return null;
  const base = 'tap-auto inline-flex items-center gap-1 text-[9px] font-mono px-1.5 py-0.5 rounded border';
  const ev = pull.last_event;
  if (pull.trouble && ev) {
    const why = ev.exit_code === 4 ? "pull tool: provider API changed (admin alerted)" : ev.exit_code === 1 ? `pull stopped: ${ev.files_failed || 0} file(s) failed` : 'pull stopped: portal or token problem';
    return <span data-testid="pull-state-trouble" title={ev.reason || ''} className={`${base} text-ug-status-red border-ug-status-red/30 bg-ug-status-red/10`}><XCircle className="w-3 h-3" /> {why}</span>;
  }
  if (pull.completed_at) {
    return <span data-testid="pull-state-completed" className={`${base} text-emerald-300 border-emerald-400/30 bg-emerald-400/10`}><CheckCircle2 className="w-3 h-3" /> Pull completed{ev?.verified_by === 'size' ? ' (size-checked)' : ''}</span>;
  }
  if (ev && ev.exit_code === 130) {
    return <span data-testid="pull-state-interrupted" className={`${base} text-slate-400 border-slate-600/40 bg-white/[0.03]`}><Clock className="w-3 h-3" /> Pull interrupted, not resumed yet</span>;
  }
  return <span data-testid="pull-state-nothing-completed" className={`${base} text-amber-300 border-amber-400/30 bg-amber-400/10`}><AlertTriangle className="w-3 h-3" /> Token issued {fmtDays(pull.days_since_issue)}, nothing completed</span>;
}

/** Editor-facing, on the Files tab, only while the last pull ended in trouble. This IS the fallback now
 *  that the NAS is going: the client's original link, in a browser. Written down, not implied. */
export function PullTroubleNote({ data, role }) {
  const pull = data?.pull;
  if (!isEditor(role) || !pull?.trouble || !pull.last_event) return null;
  const ev = pull.last_event;
  const detail = ev.exit_code === 4
    ? 'The pull tool needs a fix on our side and admin has been told.'
    : ev.exit_code === 1 ? `${ev.files_failed || 0} file(s) failed to download; re-running the tool retries just those.`
    : 'The portal or the download token was the problem; re-running the tool usually resumes.';
  return (
    <p data-testid="pull-trouble-note" className="text-[11px] leading-relaxed rounded-md border px-3 py-2 mt-2 text-amber-200/90 border-amber-400/25 bg-amber-400/[0.06]">
      The pull tool had a problem last time. {detail} You can still download this in your browser from the link above.
    </p>
  );
}

export function OrderCompleteLinkCheck({ projectId, role }) {
  const { data, settled } = useLinkChecks(projectId, { poll: true });
  const rows = data?.rows || [];
  if (rows.length === 0) return null;
  const done = rows.every(r => r.status === 'done');
  if (!done && !settled) return <p data-testid="order-link-check-pending" className="font-sans text-[11px] text-zinc-400 inline-flex items-center gap-1.5 mb-6"><Clock className="w-3.5 h-3.5 animate-pulse" /> Checking your footage link…</p>;
  const bad = rows.filter(r => ['malformed', 'dead', 'walled', 'empty'].includes(r.verdict));
  const size = data?.summary?.size;
  if (bad.length === 0 && !(size && (size.verdict === 'short' || (size.verdict === 'ok' && size.size_checked)))) return null;   // unknown / unsettled: nothing
  return (
    <div data-testid="order-link-check" className="text-left mb-6">
      {bad.map((r, i) => <LinkVerdict key={i} row={r} role={role} />)}
      <LinkCheckSummary data={data} role={role} />
    </div>
  );
}
